'use client';

import { useMemo } from 'react';

import { projectApi } from '@highlight-cp/project-api';

import { getCustomCriteriaLabelsFromTestGroups, getTaskVariablesFromTestGroups } from './utils';

export interface useCustomVariablesFromRecruitingParams {
  projectId: number | null | undefined;
}

/**
 * ### useCustomVariablesFromRecruiting
 *
 * @description
 * Returns custom variables (custom criteria labels and task variables) for opt-in/screener surveys
 * derived from project recruiting data. Uses the same logic as the recruiting slice: CustomTag
 * quota partition values as criteria labels, and "{taskType}_assigned" for each used task type.
 * Use this when you do not have access to the recruiting Redux slice (e.g. in feature-survey-config).
 *
 * @example
 * const { customVariables, customCriteriaVariables, taskVariables, isLoading } =
 *   useCustomVariablesFromRecruiting({ projectId });
 * await createSurvey({ survey: { ...survey, customVariables } });
 *
 * @param params - Object with projectId to load recruiting data for
 * @returns Object with customVariables (combined deduped), customCriteriaVariables, taskVariables, and isLoading
 */
export const useCustomVariablesFromRecruiting = (
  params: useCustomVariablesFromRecruitingParams,
) => {
  const { projectId } = params;
  const { data: recruitingData, isLoading } =
    projectApi.useGetProjectsByProjectIdRecruitingDataQuery(
      { projectId: projectId ?? 0 },
      { skip: !projectId },
    );

  const result = useMemo(() => {
    const testGroups = recruitingData?.spec?.audience?.testGroups ?? [];
    const customCriteriaVariables = getCustomCriteriaLabelsFromTestGroups(testGroups);
    const taskVariables = getTaskVariablesFromTestGroups(testGroups);
    const customVariables = [...new Set([...customCriteriaVariables, ...taskVariables])];
    return { customVariables, customCriteriaVariables, taskVariables };
  }, [recruitingData]);

  return { ...result, isLoading };
};
